/**
 * Page load analytics tracked in Firebase Realtime Database.
 *
 * Increments a global daily page-load counter and a per-user counter (keyed by
 * IP address). Used for monitoring overall site traffic and individual usage.
 */
import { ref, get, runTransaction } from 'firebase/database';
import { database } from './firebase';

/**
 * Get today's date in YYYY-MM-DD format
 * @returns {string} Date string
 */
const getTodayDateString = () => {
  const today = new Date();
  return today.toISOString().split('T')[0];
};

/**
 * Increment daily page load counter in Firebase Realtime Database
 * Uses atomic transactions to prevent race conditions
 */
export const incrementPageLoadCounter = async () => {
  try {
    const dateString = getTodayDateString();
    const counterRef = ref(database, `analytics/pageLoads/${dateString}`);
    
    // Use a transaction to atomically increment the counter
    await runTransaction(counterRef, (currentData) => {
      return (currentData || 0) + 1;
    });
    
    console.log(`📊 Page load counted for ${dateString}`);
  } catch (error) {
    console.error('Error incrementing page load counter:', error);
    // Don't throw error to avoid breaking the app if analytics fails
  }
};

/**
 * Get page load count for a specific date
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @returns {Promise<number>} Page load count
 */
export const getPageLoadCount = async (dateString) => {
  try {
    const counterRef = ref(database, `analytics/pageLoads/${dateString}`);
    const snapshot = await get(counterRef);
    return snapshot.val() || 0;
  } catch (error) {
    console.error('Error getting page load count:', error);
    return 0;
  }
};

/**
 * Get page load count for today
 * @returns {Promise<number>} Today's page load count
 */
export const getTodayPageLoadCount = async () => {
  const dateString = getTodayDateString();
  return await getPageLoadCount(dateString);
};

/**
 * Get user's IP address (using a free IP detection service)
 */
const getUserIP = async () => {
  try {
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    return data.ip;
  } catch (error) {
    console.error('Error getting IP address:', error);
    // Fallback to a placeholder if IP detection fails
    return 'unknown';
  }
};

/**
 * Sanitize IP address for use as Firebase path
 * Replace dots with underscores since dots are invalid in Firebase paths
 */
const sanitizeIPAddress = (ipAddress) => {
  return ipAddress.replace(/\./g, '_');
};

/**
 * Generate a user identifier (can be IP-based for now)
 */
const getUserId = async () => {
  try {
    const ip = await getUserIP();
    const sanitizedIP = sanitizeIPAddress(ip);
    // Use sanitized IP as user identifier for now
    // In the future, this could be enhanced with authentication
    return `user_${sanitizedIP}`;
  } catch (error) {
    console.error('Error generating user ID:', error);
    return 'user_unknown';
  }
};

/**
 * Increment page load counter for a specific user on a specific date
 * @param {string} userId - User identifier
 * @param {string} dateString - Date in YYYY-MM-DD format
 */
export const incrementUserPageLoadCounter = async (userId, dateString) => {
  try {
    const userCounterRef = ref(database, `analytics/userPageLoads/${userId}/${dateString}`);
    
    // Use a transaction to atomically increment the counter
    await runTransaction(userCounterRef, (currentData) => {
      return (currentData || 0) + 1;
    });
    
    console.log(`📊 User page load counted for ${userId} on ${dateString}`);
  } catch (error) {
    console.error('Error incrementing user page load counter:', error);
    // Don't throw error to avoid breaking the app if analytics fails
  }
};

/**
 * Track both global and per-user page loads
 */
export const trackPageLoad = async () => {
  try {
    const dateString = getTodayDateString();
    const userId = await getUserId();
    
    // Run both tracking operations in parallel
    await Promise.all([
      incrementPageLoadCounter(), // Global counter
      incrementUserPageLoadCounter(userId, dateString) // Per-user counter
    ]);
    
    console.log('📊 Page load tracked successfully');
  } catch (error) {
    console.error('Error tracking page load:', error);
    // Don't throw error to avoid breaking the app if analytics fails
  }
};

/**
 * Get page load count for a specific user on a specific date
 * @param {string} userId - User identifier
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @returns {Promise<number>} User's page load count for that date
 */
export const getUserPageLoadCount = async (userId, dateString) => {
  try {
    const userCounterRef = ref(database, `analytics/userPageLoads/${userId}/${dateString}`);
    const snapshot = await get(userCounterRef);
    return snapshot.val() || 0;
  } catch (error) {
    console.error('Error getting user page load count:', error);
    return 0;
  }
};

/**
 * Increment a named event counter for today, grouped under the date.
 * Path: analytics/events/{YYYY-MM-DD}/{eventName}
 * @param {string} eventName - e.g. 'quizLoads', 'quizCompletions', 'doctorBackedClicks'
 */
export const trackEvent = async (eventName) => {
  try {
    const dateString = getTodayDateString();
    const counterRef = ref(database, `analytics/events/${dateString}/${eventName}`);

    await runTransaction(counterRef, (currentData) => {
      return (currentData || 0) + 1;
    });
  } catch (error) {
    console.error(`Error tracking event ${eventName}:`, error);
  }
};

/**
 * Get all page load data for a specific user
 * @param {string} userId - User identifier
 * @returns {Promise<Object>} Object with dates as keys and counts as values
 */
export const getUserPageLoadHistory = async (userId) => {
  try {
    const userDataRef = ref(database, `analytics/userPageLoads/${userId}`);
    const snapshot = await get(userDataRef);
    return snapshot.val() || {};
  } catch (error) {
    console.error('Error getting user page load history:', error);
    return {};
  }
};